<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJson,
  postMarketplaceJsonBody,
  fetchMarketplaceBlob,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const data = ref(null);
const loading = ref(false);
const computing = ref(false);
const refreshing = ref(false);
const errorMsg = ref('');
// "Yenile" bastığımızda eldeki snapshot zamanı; yenisi gelene kadar yoklarız.
const pendingSince = ref(null);

const tl = n => `${Math.round(Number(n) || 0).toLocaleString('tr-TR')} TL`;

// Snapshot taze değilse / hesaplanıyorsa kendini otomatik tazeler (poll).
const POLL_MS = 10000;
let pollTimer = null;
const stopPoll = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};
const schedulePoll = () => {
  stopPoll();
  // load aşağıda tanımlı (schedulePoll ile karşılıklı çağrı); çalışma anında hazır.
  // eslint-disable-next-line no-use-before-define
  pollTimer = setTimeout(() => load({ silent: true }), POLL_MS);
};

// silent=true → arka plan tazeleme (tam ekran "Yükleniyor" gösterme).
const load = async ({ silent = false } = {}) => {
  if (!silent) loading.value = true;
  errorMsg.value = '';
  try {
    const res = await fetchMarketplaceJson(accountId, 'sku_profits/overview');
    if (res.status === 'computing') {
      computing.value = true;
      data.value = null;
      schedulePoll(); // ilk hesap bitene kadar bekle
    } else {
      computing.value = false;
      data.value = res;
      // Hâlâ beklenen taze hesap gelmedi mi? (snapshot zamanı ilerlemediyse)
      const stillPending =
        pendingSince.value && res.generated_at === pendingSince.value;
      // Snapshot bayatsa overview arka planda yeniden hesap tetikler; taze
      // sonucu yakalamak için durana kadar yoklamaya devam et.
      if (res.stale || stillPending) {
        refreshing.value = true;
        schedulePoll();
      } else {
        refreshing.value = false;
        pendingSince.value = null;
        stopPoll();
      }
    }
  } catch (e) {
    errorMsg.value = e.message || 'Veri alınamadı.';
  } finally {
    loading.value = false;
  }
};

// Tüm ürünler için fiyat + kampanya Excel'i indir (3 sayfa).
const downloading = ref(false);
const downloadXls = async () => {
  downloading.value = true;
  errorMsg.value = '';
  try {
    const blob = await fetchMarketplaceBlob(accountId, 'sku_profits/export');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `urun_fiyat_kampanya_${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch (e) {
    errorMsg.value = e.message || 'Excel indirilemedi.';
  } finally {
    downloading.value = false;
  }
};

// "Yenile" → motoru zorla yeniden hesaplat, yeni snapshot gelene kadar yokla.
const refresh = async () => {
  refreshing.value = true;
  pendingSince.value = data.value?.generated_at || null;
  try {
    await postMarketplaceJson(accountId, 'sku_profits/refresh');
  } catch (e) {
    errorMsg.value = e.message || 'Yenilenemedi.';
  }
  schedulePoll();
};

const rows = computed(() => data.value?.rows || []);

// Zam listesi: maliyet güvenilir + taban fiyatın altında. Etki sırasına göre.
const raiseList = computed(() =>
  rows.value
    .filter(r => r.needs_raise && r.cost_reliable)
    .sort(
      (a, b) =>
        (b.floor_price - b.avg_price) * b.units -
        (a.floor_price - a.avg_price) * a.units
    )
);
const reviewList = computed(() => data.value?.review_needed || []);
const returnMonsters = computed(() => data.value?.return_monsters || []);
const winners = computed(() =>
  rows.value
    .filter(
      r => r.cost_reliable && r.floor_price && r.avg_price >= r.floor_price
    )
    .sort((a, b) => b.net - a.net)
);

// Zam potansiyeli (hacim sabit varsayımı, kaba).
const raisePotential = computed(() =>
  raiseList.value.reduce(
    (s, r) => s + (r.floor_price - r.avg_price) * r.units,
    0
  )
);
const lossCount = computed(() => (data.value?.loss_makers || []).length);

const summary = computed(() => {
  if (!data.value) return '';
  return (
    `${raiseList.value.length} üründe satış fiyatın gerçek maliyeti karşılamıyor — ` +
    `bunların ${lossCount.value} tanesi şu an doğrudan zarar ettiriyor. ` +
    `Önerilen fiyatlar uygulanırsa kabaca ${tl(raisePotential.value)} ek kâr/marj ` +
    `çıkar (talep biraz düşerse altı). Trendyol'a önce uygula — zarar orada. ` +
    `${winners.value.length} ürün zaten sağlıklı, onlara dokunma.`
  );
});

// Kategori üyelik kuralları (sunucudaki kova mantığının birebir aynısı).
const isRaise = r => r.needs_raise && r.cost_reliable;
const isReview = r => r.net < 0 && !r.cost_reliable;
const isReturn = r => r.return_rate != null && r.return_rate >= 20;
const isAlarm = r => r.channel_price_alarm === true;
const isWinner = r =>
  r.cost_reliable && r.floor_price && r.avg_price >= r.floor_price;

// Tek satıra düşen birincil rozet (öncelik: incele > iade > zam > kazanan).
const primaryCat = r => {
  if (isReview(r))
    return { label: '⚫ İncele', cls: 'bg-n-solid-3 text-n-slate-11' };
  if (isReturn(r))
    return {
      label: '🟠 İade Canavarı',
      cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    };
  if (isRaise(r))
    return {
      label: '🔴 Zam',
      cls: 'bg-ruby-100 text-ruby-800 dark:bg-ruby-900/30 dark:text-ruby-300',
    };
  if (isWinner(r))
    return {
      label: '🟢 Kazanan',
      cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    };
  return { label: '◽ Diğer', cls: 'bg-n-solid-2 text-n-slate-10' };
};

const catFilter = ref('all');
const categories = computed(() => [
  { key: 'all', label: 'Hepsi', count: rows.value.length },
  { key: 'raise', label: '🔴 Zam', count: raiseList.value.length },
  { key: 'review', label: '⚫ İncele', count: reviewList.value.length },
  {
    key: 'returns',
    label: '🟠 İade Canavarı',
    count: returnMonsters.value.length,
  },
  {
    key: 'gap',
    label: '🔺 Kanal Farkı',
    count: rows.value.filter(isAlarm).length,
  },
  { key: 'winners', label: '🟢 Kazanan', count: winners.value.length },
]);

// Kolon başlığı filtreleri (metin + "en az" sayısal eşikler).
const fText = ref('');
const fUnits = ref('');
const fAvg = ref('');
const fFloor = ref('');
const fRec = ref('');
const fRet = ref('');
const fNet = ref('');

const gte = (val, ref_) =>
  ref_ === '' || ref_ == null || Number(val || 0) >= Number(ref_);

const clearFilters = () => {
  catFilter.value = 'all';
  fText.value = '';
  fUnits.value = '';
  fAvg.value = '';
  fFloor.value = '';
  fRec.value = '';
  fRet.value = '';
  fNet.value = '';
};

const matchesCat = r => {
  if (catFilter.value === 'raise') return isRaise(r);
  if (catFilter.value === 'review') return isReview(r);
  if (catFilter.value === 'returns') return isReturn(r);
  if (catFilter.value === 'gap') return isAlarm(r);
  if (catFilter.value === 'winners') return isWinner(r);
  return true;
};

const matchesFilter = r => {
  const q = fText.value.trim().toLowerCase();
  const text =
    q === '' || `${r.title || ''} ${r.code}`.toLowerCase().includes(q);
  return (
    matchesCat(r) &&
    text &&
    gte(r.units, fUnits.value) &&
    gte(r.avg_price, fAvg.value) &&
    gte(r.floor_price, fFloor.value) &&
    gte(r.recommended_price, fRec.value) &&
    gte(r.return_rate, fRet.value) &&
    gte(r.net, fNet.value)
  );
};

// Tek liste: tüm ürünler, en çok zarardan kâra doğru. Filtre kolondan.
const displayRows = computed(() =>
  rows.value
    .filter(matchesFilter)
    .slice()
    .sort((a, b) => a.net - b.net)
);

const genAt = computed(() => {
  const t = data.value?.generated_at;
  return t ? new Date(t).toLocaleString('tr-TR') : '';
});

const assumptions = computed(() => data.value?.assumptions || null);

// --- Yeni ürün fiyat hesaplayıcı ---
const calcCost = ref('');
const calcReturn = ref('');
const calcMargin = ref('');
const calcResult = ref(null);
const calcLoading = ref(false);
const calcError = ref('');

// Pazaryeri kartının "güvenli indirim payı" (önerilen → sürdürülebilir aralığı).
const campaignBand = computed(() => {
  const q = (calcResult.value?.quotes || []).find(x => x.type === 'pazaryeri');
  if (!q || !q.recommended_price || !q.sustainable_price) return null;
  const room = q.recommended_price - q.sustainable_price;
  if (room <= 0) return null;
  return {
    rec: q.recommended_price,
    sus: q.sustainable_price,
    room,
    pct: Math.round((room / q.recommended_price) * 100),
  };
});

const calcRun = async () => {
  if (!calcCost.value || Number(calcCost.value) <= 0) {
    calcError.value = 'Önce maliyet fiyatını gir.';
    return;
  }
  calcLoading.value = true;
  calcError.value = '';
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      'sku_profits/calculate',
      {
        unit_cost: Number(calcCost.value),
        return_rate_pct:
          calcReturn.value === '' ? null : Number(calcReturn.value),
        target_margin_pct:
          calcMargin.value === '' ? null : Number(calcMargin.value),
      }
    );
    if (res.error) {
      calcError.value = res.error;
      calcResult.value = null;
    } else {
      calcResult.value = res;
    }
  } catch (e) {
    calcError.value = e.message || 'Hesaplanamadı.';
  } finally {
    calcLoading.value = false;
  }
};

// --- Sicil belgesi (ürün detay) ---
const selected = ref(null);
const openDetail = r => {
  selected.value = r;
};
const closeDetail = () => {
  selected.value = null;
};

const fmtDate = iso => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '—');

const detailSummary = computed(() => {
  const r = selected.value;
  if (!r) return '';
  const parts = [];
  parts.push(
    `Son 30 günde ${r.sold_30d} adet sattı (günde ~${r.velocity_per_day}).`
  );
  if (r.stock_on_hand > 0) {
    parts.push(
      `Stokta ${r.stock_on_hand} adet var` +
        (r.days_to_stockout != null
          ? ` — bu hızla ~${r.days_to_stockout} günde biter.`
          : '.')
    );
  } else {
    parts.push('Stok tükenmiş (0 adet).');
  }
  if (r.return_rate != null) {
    parts.push(
      `İade oranı %${r.return_rate}` +
        (r.return_loss ? ` (iadeden ~${tl(r.return_loss)} kargo kaybı).` : '.')
    );
  }
  const ty = (r.channel_pricing || []).find(c => c.type === 'pazaryeri');
  if (ty && ty.recommended_price) {
    parts.push(
      `Trendyol'da şu an ort. ${tl(ty.avg_price)}; sağlıklı taban ${tl(
        ty.floor_price
      )}, %15 net için önerilen ${tl(ty.recommended_price)}.`
    );
  }
  return parts.join(' ');
});

onMounted(() => load());
onUnmounted(stopPoll);
</script>

<template>
  <div class="w-full h-full overflow-auto bg-n-background">
    <div class="p-6 max-w-[1200px] mx-auto">
      <div class="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 class="text-2xl font-black text-n-slate-12">
          Ürün Fiyat & İade Motoru
        </h1>
        <div class="flex items-center gap-2">
          <span
            v-if="refreshing || computing"
            class="text-sm text-amber-700 dark:text-amber-300 font-semibold flex items-center gap-1"
          >
            <span class="i-lucide-loader-circle animate-spin" /> Veriler
            yenileniyor…
          </span>
          <button
            class="px-4 py-2 rounded-xl text-base font-semibold bg-n-solid-2 text-n-slate-12 border border-n-slate-5 hover:bg-n-solid-3 disabled:opacity-50"
            :disabled="refreshing || computing"
            @click="refresh"
          >
            <span class="i-lucide-rotate-cw" /> Yeniden Hesapla
          </button>
          <button
            v-if="rows.length"
            class="px-4 py-2 rounded-xl text-base font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            :disabled="downloading"
            @click="downloadXls"
          >
            <span class="i-lucide-download" />
            {{
              downloading
                ? 'Hazırlanıyor…'
                : 'Tüm Ürünler — Fiyat + Kampanya (Excel)'
            }}
          </button>
        </div>
      </div>

      <p v-if="loading" class="text-lg text-n-slate-11 py-10 text-center">
        Yükleniyor…
      </p>

      <div
        v-else-if="computing"
        class="rounded-2xl p-8 bg-amber-50 border-2 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700 text-center"
      >
        <p class="text-xl font-bold text-amber-800 dark:text-amber-300 mb-2">
          Motor hesaplanıyor…
        </p>
        <p class="text-lg text-n-slate-11">
          Tüm siparişler taranıyor (1-2 dakika sürebilir). Bir-iki dakika sonra
          “Yenile”ye bas.
        </p>
      </div>

      <p
        v-else-if="errorMsg"
        class="text-lg text-ruby-700 py-10 text-center font-semibold"
      >
        {{ errorMsg }}
      </p>

      <template v-else-if="data">
        <!-- HERO: sade Türkçe özet -->
        <div
          class="rounded-2xl p-6 mb-5 border-2 bg-n-solid-1 border-n-slate-5"
        >
          <div class="text-lg font-semibold text-n-slate-11 mb-1">
            Durum özeti
          </div>
          <p class="text-xl leading-relaxed text-n-slate-12 font-medium">
            {{ summary }}
          </p>
          <div class="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
            <div class="rounded-xl bg-ruby-50 dark:bg-ruby-900/20 p-3">
              <div class="text-3xl font-black text-ruby-700 tabular-nums">
                {{ raiseList.length }}
              </div>
              <div class="text-sm text-n-slate-11">Zam yapılacak</div>
            </div>
            <div class="rounded-xl bg-green-50 dark:bg-green-900/20 p-3">
              <div class="text-3xl font-black text-green-700 tabular-nums">
                +{{ tl(raisePotential) }}
              </div>
              <div class="text-sm text-n-slate-11">Tahmini ek kâr</div>
            </div>
            <div class="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
              <div class="text-3xl font-black text-amber-700 tabular-nums">
                {{ returnMonsters.length }}
              </div>
              <div class="text-sm text-n-slate-11">İade canavarı</div>
            </div>
            <div class="rounded-xl bg-n-solid-2 p-3">
              <div class="text-3xl font-black text-n-slate-12 tabular-nums">
                {{ winners.length }}
              </div>
              <div class="text-sm text-n-slate-11">Sağlıklı ürün</div>
            </div>
          </div>
          <p v-if="genAt" class="mt-3 text-sm text-n-slate-10">
            Son güncelleme: {{ genAt }} · Tüm rakamlar gerçek sipariş/maliyet
            verisinden canlı hesaplanır; veri 6 saatte bir otomatik tazelenir,
            anında istersen “Yeniden Hesapla”ya bas.
            <span
              v-if="data && data.stale"
              class="text-amber-700"
              >(şu an tazeleniyor…)</span
            >
          </p>
          <p
            v-if="assumptions"
            class="mt-1 text-sm text-n-slate-10 leading-relaxed"
          >
            Fiyatlara yedirilen yükler: işletme gideri (maaş/SGK/vergi/fatura)
            <b>%{{ assumptions.overhead_rate_pct }}</b> · reklam
            <b>%{{ assumptions.ad_rate_pct }}</b> · Trendyol platform
            <b>%{{ assumptions.trendyol_platform_rate_pct }}</b> + diğer
            <b>%{{ assumptions.trendyol_other_rate_pct }}</b> · hedef net
            <b>%{{ assumptions.target_margin_pct }}</b> · iade değer kaybı
            <b>%{{ assumptions.resale_loss_pct }}</b> · site vitrini Trendyol'un
            <b>%{{ assumptions.site_discount_pct }}</b> altı. Bir ürüne tıkla →
            sicil belgesi.
          </p>
        </div>

        <!-- YENİ ÜRÜN FİYAT HESAPLAYICI -->
        <div
          class="rounded-2xl p-5 mb-5 border-2 border-violet-300 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-700"
        >
          <div class="text-lg font-black text-n-slate-12 mb-1">
            🧮 Yeni Ürün Fiyat Hesapla
          </div>
          <p class="text-base text-n-slate-11 mb-4">
            Maliyeti <b>dolar ($) ve KDV hariç</b> gir, “Hesapla”ya bas — üstüne
            <b>%10 KDV</b> ekleyip <b>güncel TCMB kuruyla</b> TL’ye çeviririm,
            sonra satış fiyatını hesaplarım. Her kanal için 🟢 önerilen satış,
            🟡 sürdürülebilir (kâr 0), 🔴 min (altına nakit zarar) verilir; site
            kartında ayrıca <b>🏷️ vitrin fiyatı</b> (müşteriye yazacağın,
            pazaryeri fiyatının <b>%10 altı</b>). Fiyata iade kargosu + <b>iade
            değer kaybı</b> de yedirildi. Trendyol komisyonu güvenli tarafta
            <b>standart %21</b> alınır.
          </p>
          <div class="flex items-end gap-3 flex-wrap">
            <div>
              <label class="block text-sm font-semibold text-n-slate-11 mb-1">
                Maliyet ($, KDV hariç) *
              </label>
              <input
                v-model="calcCost"
                type="number"
                placeholder="örn. 12"
                class="w-32 text-lg font-bold rounded-xl border-2 border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-3 py-2"
                @keyup.enter="calcRun"
              />
            </div>
            <div>
              <label class="block text-sm font-semibold text-n-slate-11 mb-1">
                İade % (ops.)
              </label>
              <input
                v-model="calcReturn"
                type="number"
                placeholder="ort."
                class="w-24 text-base rounded-xl border-2 border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-3 py-2"
                @keyup.enter="calcRun"
              />
            </div>
            <div>
              <label class="block text-sm font-semibold text-n-slate-11 mb-1">
                Kâr % (ops.)
              </label>
              <input
                v-model="calcMargin"
                type="number"
                placeholder="15"
                class="w-24 text-base rounded-xl border-2 border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-3 py-2"
                @keyup.enter="calcRun"
              />
            </div>
            <button
              class="px-6 py-2.5 rounded-xl text-lg font-bold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              :disabled="calcLoading"
              @click="calcRun"
            >
              {{ calcLoading ? 'Hesaplanıyor…' : 'Hesapla' }}
            </button>
          </div>

          <p
            v-if="calcError"
            class="mt-3 text-base text-ruby-700 font-semibold"
          >
            {{ calcError }}
          </p>

          <!-- DÖVİZ / KDV ÇEVRİMİ ÖZETİ -->
          <p
            v-if="calcResult"
            class="mt-4 text-base text-n-slate-11 bg-n-solid-2 rounded-xl px-4 py-2"
          >
            💱 Maliyet: <b>${{ calcResult.usd_cost }}</b> × kur
            <b>{{ calcResult.usd_rate }} TL</b>
            <span v-if="calcResult.usd_rate_date">
              (TCMB {{ calcResult.usd_rate_date }})</span
            >
            + %{{ calcResult.kdv_pct }} KDV =
            <b class="text-n-slate-12">{{ tl(calcResult.unit_cost) }}</b> TL
            maliyet. Aşağıdaki tüm fiyatlar bu TL maliyetten hesaplandı.
          </p>

          <div
            v-if="calcResult"
            class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3"
          >
            <div
              v-for="q in calcResult.quotes"
              :key="q.channel"
              class="rounded-xl border-2 p-4 bg-n-solid-1"
              :class="
                q.type === 'pazaryeri' ? 'border-violet-400' : 'border-sky-400'
              "
            >
              <div class="flex items-center justify-between mb-2">
                <span class="text-base font-bold text-n-slate-12">
                  {{
                    q.type === 'pazaryeri'
                      ? 'Pazaryeri Satış Fiyatı'
                      : 'Site Satış Fiyatı'
                  }}
                </span>
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-n-solid-2 text-n-slate-11 capitalize"
                >
                  {{ q.channel }}
                </span>
              </div>
              <!-- SİTE VİTRİN FİYATI (müşteriye gösterilecek, önerilenin üstünde) -->
              <div
                v-if="q.type === 'site'"
                class="rounded-lg px-3 py-2 mb-3 bg-sky-100 dark:bg-sky-900/30"
              >
                <div class="text-sm font-semibold text-n-slate-11">
                  🏷️ Vitrin fiyatı (müşteriye yaz)
                </div>
                <div class="text-3xl font-black text-n-slate-12 tabular-nums">
                  {{ tl(q.list_price) }}
                </div>
                <div class="text-sm text-sky-700 dark:text-sky-300">
                  Pazaryeri fiyatının %{{
                    calcResult.defaults.site_discount_pct
                  }}
                  altı — müşteri "indirim" görür, "kazık" demez.
                </div>
              </div>
              <div class="flex items-baseline justify-between mb-1">
                <span class="text-base text-n-slate-10">🟢 Önerilen satış</span>
                <span class="text-xl font-black text-green-700 tabular-nums">
                  {{ tl(q.recommended_price) }}
                </span>
              </div>
              <div class="flex items-baseline justify-between mb-1">
                <span class="text-base text-n-slate-10"
                  >🟡 Sürdürülebilir (kâr 0)</span
                >
                <span
                  class="text-lg font-semibold text-n-slate-12 tabular-nums"
                >
                  {{ tl(q.sustainable_price) }}
                </span>
              </div>
              <div class="flex items-baseline justify-between">
                <span class="text-base text-n-slate-10"
                  >🔴 Min (altına = nakit zarar)</span
                >
                <span
                  class="text-base font-semibold text-ruby-600 tabular-nums"
                >
                  {{ tl(q.min_price) }}
                </span>
              </div>
              <div
                v-if="q.recommended_campaign"
                class="mt-2 text-sm text-n-slate-11 bg-n-solid-2 rounded-lg px-2 py-1"
              >
                Komisyon %{{ q.commission_pct }} (standart) alındı. Kampanyaya
                girersen önerilen ≈ {{ tl(q.recommended_campaign) }} (kâr
                artar).
              </div>
              <div class="mt-2 text-sm text-n-slate-10">
                Toplam yük %{{ q.fee_full_pct }} · iade %{{
                  q.return_rate_pct
                }}
                · kâr %{{ q.margin_pct }}
              </div>
            </div>
          </div>

          <!-- KAMPANYA / İNDİRİM PAYI REHBERİ -->
          <div
            v-if="calcResult"
            class="mt-3 rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-4"
          >
            <div class="text-base font-black text-n-slate-12 mb-2">
              📣 Kampanya / indirim payın — fiyatı nereye kadar kırabilirsin?
            </div>
            <p class="text-base text-n-slate-11 leading-relaxed mb-2">
              Üstteki üç renk, satış politikanın sınırlarıdır. Trendyol/pazaryeri
              kampanyasına girerken <b>son müşteri fiyatının</b> hangi bantta
              kaldığına bak:
            </p>
            <ul class="text-base text-n-slate-11 leading-relaxed space-y-1.5">
              <li>
                🟢 <b>Önerilen satış ve üstü:</b> tam sağlık. Hedeflediğin
                <b>%{{ calcResult.defaults.margin_pct }} net kâr</b> cebinde.
              </li>
              <li>
                🟢→🟡 <b>Önerilen ile Sürdürülebilir arası</b> — işte
                <b>güvenli kampanya payın budur.</b> Burada kârından veriyorsun
                ama komisyon, kargo, reklam ve işletme giderlerinin
                <b>hepsi hâlâ karşılanıyor</b>. İndirimi rahatça yapabilirsin;
                yeter ki son fiyat 🟡’nin altına düşmesin.
              </li>
              <li>
                🟡→🔴 <b>Sürdürülebilir ile Min arası</b> — tehlikeli bölge.
                Artık işletme giderini (maaş/kira/fatura) karşılamıyorsun, sadece
                ürün + komisyon + kargoyu kurtarıyorsun. Yalnızca <b>stok eritme
                / nakit döndürme</b> için, kısa süreli kullan.
              </li>
              <li>
                🔴 <b>Min’in altı:</b> her satışta cebinden para çıkar. Asla bu
                fiyata satma.
              </li>
            </ul>
            <p
              v-if="campaignBand"
              class="mt-3 text-base text-n-slate-12 bg-n-solid-1 rounded-lg px-3 py-2"
            >
              👉 <b>Pazaryeri için:</b> önerilen {{ tl(campaignBand.rec) }} →
              sürdürülebilir {{ tl(campaignBand.sus) }} arasında
              <b>{{ tl(campaignBand.room) }}</b> ({{ campaignBand.pct }}%)
              güvenli indirim payın var. Son müşteri fiyatını
              {{ tl(campaignBand.sus) }} altına indirme.
            </p>
            <p class="mt-2 text-sm text-n-slate-10 leading-relaxed">
              ⚠️ Trendyol kampanyada genelde <b>kendi ek indirimini de</b>
              (kupon / sepet indirimi) fiyatın üstüne bindirir. İndirim oranını
              belirlerken bunu da hesaba kat — kampanya sonrası ekranda görünen
              son fiyat 🟡 Sürdürülebilir’in altına inmemeli.
            </p>
          </div>
        </div>

        <div class="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div class="text-base text-n-slate-11">
            Tüm ürünler ({{ rows.length }}) —
            <b class="text-n-slate-12">{{ displayRows.length }}</b> gösteriliyor
          </div>
        </div>

        <!-- Tek liste: tüm ürünler. Durum kolonundan filtre. -->
        <div
          class="rounded-2xl border border-n-slate-5 bg-n-solid-1 overflow-hidden"
        >
          <table class="w-full text-base">
            <thead>
              <tr class="bg-n-solid-2 text-n-slate-11 text-left">
                <th class="px-3 pt-3 font-semibold">Ürün</th>
                <th class="px-3 pt-3 font-semibold">Durum</th>
                <th class="px-3 pt-3 font-semibold text-right">Adet</th>
                <th class="px-3 pt-3 font-semibold text-right">Mevcut</th>
                <th class="px-3 pt-3 font-semibold text-right">Taban</th>
                <th class="px-3 pt-3 font-semibold text-right">Önerilen</th>
                <th class="px-3 pt-3 font-semibold text-right">İade</th>
                <th class="px-3 pt-3 font-semibold text-right">Net</th>
                <th class="px-3 pt-3 font-semibold text-right" />
              </tr>
              <!-- Filtre satırı: her başlıkta filtre -->
              <tr class="bg-n-solid-2 align-top">
                <th class="px-3 pb-3">
                  <input
                    v-model="fText"
                    type="text"
                    placeholder="Ürün / kod ara"
                    class="w-full text-sm font-normal rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  />
                </th>
                <th class="px-3 pb-3">
                  <select
                    v-model="catFilter"
                    class="w-full text-sm font-normal rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  >
                    <option v-for="c in categories" :key="c.key" :value="c.key">
                      {{ c.label }} ({{ c.count }})
                    </option>
                  </select>
                </th>
                <th class="px-3 pb-3">
                  <input
                    v-model="fUnits"
                    type="number"
                    placeholder="≥"
                    class="w-20 text-sm font-normal text-right rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  />
                </th>
                <th class="px-3 pb-3">
                  <input
                    v-model="fAvg"
                    type="number"
                    placeholder="≥ ₺"
                    class="w-24 text-sm font-normal text-right rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  />
                </th>
                <th class="px-3 pb-3">
                  <input
                    v-model="fFloor"
                    type="number"
                    placeholder="≥ ₺"
                    class="w-24 text-sm font-normal text-right rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  />
                </th>
                <th class="px-3 pb-3">
                  <input
                    v-model="fRec"
                    type="number"
                    placeholder="≥ ₺"
                    class="w-24 text-sm font-normal text-right rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  />
                </th>
                <th class="px-3 pb-3">
                  <input
                    v-model="fRet"
                    type="number"
                    placeholder="≥ %"
                    class="w-20 text-sm font-normal text-right rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  />
                </th>
                <th class="px-3 pb-3">
                  <input
                    v-model="fNet"
                    type="number"
                    placeholder="≥ ₺"
                    class="w-24 text-sm font-normal text-right rounded-lg border border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1"
                  />
                </th>
                <th class="px-3 pb-3 text-right">
                  <button
                    class="text-sm font-semibold text-woot-600 hover:underline whitespace-nowrap"
                    @click="clearFilters"
                  >
                    Temizle
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in displayRows"
                :key="r.code"
                class="border-t border-n-slate-4 cursor-pointer hover:bg-n-solid-2"
                @click="openDetail(r)"
              >
                <td class="p-3">
                  <div class="flex items-center gap-3">
                    <img
                      v-if="r.image"
                      :src="r.image"
                      class="w-10 h-10 rounded object-cover shrink-0"
                      alt=""
                    />
                    <div>
                      <div class="font-semibold text-n-slate-12">
                        {{ r.title || r.code }}
                      </div>
                      <div class="text-sm text-n-slate-10">
                        {{ r.code }}
                        <span
                          v-if="r.channel_price_alarm"
                          class="text-ruby-600 font-semibold"
                        >
                          · 🔺 kanal farkı %{{ r.channel_price_gap_pct }}</span
                        >
                        <span v-if="r.review_reason" class="text-amber-600">
                          · {{ r.review_reason }}</span
                        >
                      </div>
                    </div>
                  </div>
                </td>
                <td class="p-3">
                  <span
                    class="inline-block px-2.5 py-1 rounded-full text-sm font-semibold whitespace-nowrap"
                    :class="primaryCat(r).cls"
                  >
                    {{ primaryCat(r).label }}
                  </span>
                </td>
                <td class="p-3 text-right tabular-nums text-n-slate-11">
                  {{ r.units }}
                </td>
                <td class="p-3 text-right tabular-nums text-n-slate-11">
                  {{ r.avg_price ? tl(r.avg_price) : '—' }}
                </td>
                <td class="p-3 text-right tabular-nums text-n-slate-11">
                  {{ r.floor_price ? tl(r.floor_price) : '—' }}
                </td>
                <td
                  class="p-3 text-right tabular-nums font-bold text-green-700"
                >
                  {{ r.recommended_price ? tl(r.recommended_price) : '—' }}
                </td>
                <td
                  class="p-3 text-right tabular-nums"
                  :class="
                    r.return_rate >= 20
                      ? 'text-ruby-600 font-bold'
                      : 'text-n-slate-11'
                  "
                >
                  {{ r.return_rate != null ? `%${r.return_rate}` : '—' }}
                </td>
                <td
                  class="p-3 text-right tabular-nums font-semibold"
                  :class="r.net < 0 ? 'text-ruby-600' : 'text-n-slate-12'"
                >
                  {{ tl(r.net) }}
                </td>
                <td class="p-3 text-right text-n-slate-9">
                  <span class="i-lucide-chevron-right" />
                </td>
              </tr>
              <tr v-if="!displayRows.length">
                <td colspan="9" class="p-6 text-center text-n-slate-10">
                  Bu filtrede ürün yok.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- SİCİL BELGESİ — ürün detay -->
    <div
      v-if="selected"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-auto"
      @click.self="closeDetail"
    >
      <div
        class="bg-n-background rounded-2xl w-full max-w-[920px] my-6 border border-n-slate-5 shadow-xl"
      >
        <!-- başlık -->
        <div
          class="flex items-center gap-4 p-5 border-b border-n-slate-5 sticky top-0 bg-n-background rounded-t-2xl"
        >
          <img
            v-if="selected.image"
            :src="selected.image"
            class="w-16 h-16 rounded-lg object-cover shrink-0"
            alt=""
          />
          <div class="flex-1 min-w-0">
            <div class="text-xl font-black text-n-slate-12 truncate">
              {{ selected.title || selected.code }}
            </div>
            <div class="text-base text-n-slate-10">
              Model {{ selected.code }} ·
              <span
                :class="{
                  'text-green-700': selected.cost_confidence === 'yuksek',
                  'text-amber-600': selected.cost_confidence === 'tahmini',
                  'text-ruby-600':
                    selected.cost_confidence === 'tutarsiz' ||
                    selected.cost_confidence === 'eksik',
                }"
              >
                maliyet güveni: {{ selected.cost_confidence }}
              </span>
            </div>
          </div>
          <button
            class="px-3 py-2 rounded-xl text-base font-semibold bg-n-solid-2 text-n-slate-11 hover:bg-n-solid-3"
            @click="closeDetail"
          >
            Kapat ✕
          </button>
        </div>

        <div class="p-5">
          <!-- AI özet -->
          <p
            class="text-lg leading-relaxed text-n-slate-12 font-medium mb-5 bg-n-solid-1 rounded-xl p-4 border border-n-slate-4"
          >
            {{ detailSummary }}
          </p>

          <!-- 4 blok -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <!-- Satış -->
            <div class="rounded-xl border border-n-slate-4 bg-n-solid-1 p-4">
              <div class="text-base font-bold text-n-slate-12 mb-3">
                📈 Satış
              </div>
              <div class="grid grid-cols-2 gap-y-2 text-base">
                <div class="text-n-slate-10">Son 7 gün</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ selected.sold_7d }} adet
                </div>
                <div class="text-n-slate-10">Son 30 gün</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ selected.sold_30d }} adet
                </div>
                <div class="text-n-slate-10">Son 90 gün</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ selected.sold_90d }} adet
                </div>
                <div class="text-n-slate-10">Toplam</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ selected.sold_all }} adet
                </div>
                <div class="text-n-slate-10">Günlük hız</div>
                <div class="text-right font-semibold tabular-nums">
                  ~{{ selected.velocity_per_day }}/gün
                </div>
                <div class="text-n-slate-10">Son satış</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ fmtDate(selected.last_sale) }}
                </div>
              </div>
            </div>

            <!-- İade -->
            <div class="rounded-xl border border-n-slate-4 bg-n-solid-1 p-4">
              <div class="text-base font-bold text-n-slate-12 mb-3">
                ↩️ İade
              </div>
              <div class="grid grid-cols-2 gap-y-2 text-base">
                <div class="text-n-slate-10">İade oranı</div>
                <div
                  class="text-right font-semibold tabular-nums"
                  :class="selected.return_rate >= 20 ? 'text-ruby-600' : ''"
                >
                  %{{ selected.return_rate ?? 0 }}
                </div>
                <div class="text-n-slate-10">İade adedi</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ selected.returned_units }} adet
                </div>
                <div class="text-n-slate-10">Son 30 gün iade</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ selected.returned_30d }} adet
                </div>
                <div class="text-n-slate-10">İade kargo kaybı</div>
                <div
                  class="text-right font-semibold tabular-nums text-ruby-600"
                >
                  {{ tl(selected.return_loss) }}
                </div>
              </div>
            </div>

            <!-- Stok -->
            <div class="rounded-xl border border-n-slate-4 bg-n-solid-1 p-4">
              <div class="text-base font-bold text-n-slate-12 mb-3">
                📦 Stok
              </div>
              <div class="grid grid-cols-2 gap-y-2 text-base">
                <div class="text-n-slate-10">Mevcut stok</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ selected.stock_on_hand }} adet
                </div>
                <div class="text-n-slate-10">Stok maliyeti</div>
                <div class="text-right font-semibold tabular-nums">
                  {{
                    selected.stock_cost_value != null
                      ? tl(selected.stock_cost_value)
                      : '—'
                  }}
                </div>
                <div class="text-n-slate-10">Tahmini tükeniş</div>
                <div class="text-right font-semibold tabular-nums">
                  {{
                    selected.days_to_stockout != null
                      ? `~${selected.days_to_stockout} gün`
                      : '—'
                  }}
                </div>
                <div class="text-n-slate-10">Stoktan beklenen kâr</div>
                <div
                  class="text-right font-semibold tabular-nums"
                  :class="
                    selected.expected_profit_stock < 0
                      ? 'text-ruby-600'
                      : 'text-green-700'
                  "
                >
                  {{ tl(selected.expected_profit_stock) }}
                </div>
              </div>
            </div>

            <!-- Finans -->
            <div class="rounded-xl border border-n-slate-4 bg-n-solid-1 p-4">
              <div class="text-base font-bold text-n-slate-12 mb-3">
                💰 Finans (dönem)
              </div>
              <div class="grid grid-cols-2 gap-y-2 text-base">
                <div class="text-n-slate-10">Toplam ciro</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ tl(selected.revenue) }}
                </div>
                <div class="text-n-slate-10">Birim maliyet</div>
                <div class="text-right font-semibold tabular-nums">
                  {{
                    selected.unit_cost != null ? tl(selected.unit_cost) : '—'
                  }}
                </div>
                <div class="text-n-slate-10">Komisyon</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ tl(selected.commission) }}
                </div>
                <div class="text-n-slate-10">Kargo</div>
                <div class="text-right font-semibold tabular-nums">
                  {{ tl(selected.cargo) }}
                </div>
                <div class="text-n-slate-10">Net (overhead öncesi)</div>
                <div
                  class="text-right font-bold tabular-nums"
                  :class="selected.net < 0 ? 'text-ruby-600' : 'text-green-700'"
                >
                  {{ tl(selected.net) }}
                </div>
                <div class="text-n-slate-10">Net marj</div>
                <div class="text-right font-semibold tabular-nums">
                  {{
                    selected.net_margin_pct != null
                      ? `%${selected.net_margin_pct}`
                      : '—'
                  }}
                </div>
              </div>
            </div>
          </div>

          <!-- Kanal bazlı fiyatlandırma -->
          <div class="text-base font-bold text-n-slate-12 mb-3">
            🏷️ Kanal Bazlı Fiyatlandırma (pazaryeri ≠ site)
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div
              v-for="c in selected.channel_pricing || []"
              :key="c.channel"
              class="rounded-xl border-2 p-4"
              :class="
                c.type === 'pazaryeri'
                  ? 'border-violet-300 bg-violet-50 dark:bg-violet-900/20'
                  : 'border-sky-300 bg-sky-50 dark:bg-sky-900/20'
              "
            >
              <div class="flex items-center justify-between mb-2">
                <div class="font-bold text-n-slate-12 capitalize">
                  {{ c.channel }}
                </div>
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-n-solid-2 text-n-slate-11"
                >
                  {{ c.type }}
                </span>
              </div>
              <div class="grid grid-cols-2 gap-y-1.5 text-base">
                <div class="text-n-slate-10">Mevcut</div>
                <div class="text-right tabular-nums">{{ tl(c.avg_price) }}</div>
                <div class="text-n-slate-10">🏷️ Vitrin (müşteriye)</div>
                <div class="text-right tabular-nums font-black text-n-slate-12">
                  {{ c.list_price != null ? tl(c.list_price) : '—' }}
                </div>
                <div class="text-n-slate-10">Taban (gizli sınır)</div>
                <div class="text-right tabular-nums font-semibold">
                  {{ c.floor_price != null ? tl(c.floor_price) : '—' }}
                </div>
                <div class="text-n-slate-10">Önerilen</div>
                <div class="text-right tabular-nums font-black text-green-700">
                  {{
                    c.recommended_price != null ? tl(c.recommended_price) : '—'
                  }}
                </div>
                <div class="text-n-slate-10">Adet / İade</div>
                <div class="text-right tabular-nums">
                  {{ c.units }} / %{{ c.return_rate }}
                </div>
                <div class="text-n-slate-10">Toplam yük</div>
                <div class="text-right tabular-nums">%{{ c.fee_rate_pct }}</div>
              </div>
            </div>
          </div>
          <p
            v-if="selected.channel_price_alarm"
            class="mt-4 text-base text-ruby-800 dark:text-ruby-300 bg-ruby-50 dark:bg-ruby-900/20 rounded-xl p-3 font-medium"
          >
            🔺
            <b
              >Kanal fiyat farkı tehlikeli (%{{
                selected.channel_price_gap_pct
              }}).</b
            >
            Bu ürün Trendyol'da sitenden çok pahalı görünüyor — müşteri farkı
            görüp iade edebilir. Trendyol fiyatını düşür ya da site vitrinini
            yükselt (ideal fark %10–15).
          </p>
          <p
            v-if="!selected.cost_reliable"
            class="mt-4 text-base text-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3"
          >
            ⚠️ Bu üründe maliyet güvenilir değil ({{ selected.review_reason }}).
            Fiyat önerisi gösterilmedi; önce gerçek maliyeti doğrula.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
